'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import { Maximize } from 'lucide-react';
import { useRef } from 'react';
import { cn } from '../utils/cn';

const logger = createLogger('client:component:video-embed');

export interface VideoEmbedProps {
  src: string;
  title: string;
  className?: string;
  iframeClassName?: string;
  /** Aspect ratio like '16/9'. Optional. */
  aspectRatio?: string;
  allow?: string;
  isMaximized?: boolean;
}

/**
 * Extremely lightweight video embed component.
 * No loading states, no thumbnails, no timeouts, no refs.
 * Just a stable iframe wrapped inside a styled div.
 */
export function VideoEmbed({
  src,
  title,
  className,
  iframeClassName,
  aspectRatio = '16/9',
  allow = 'autoplay; fullscreen; picture-in-picture',
  isMaximized = false,
}: VideoEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleFullscreen = async () => {
    try {
      // Use browser's Fullscreen API
      if (containerRef.current) {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        } else {
          await containerRef.current.requestFullscreen();
        }
      }
    } catch (error) {
      logger.error('Failed to toggle fullscreen:', error);
    }
  };
  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full overflow-hidden rounded-xl glass-surface-bg glass-surface-border border border-border/40',
        className
      )}
      style={{
        aspectRatio,
      }}
    >
      {!isMaximized && (
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={handleFullscreen}
            className="h-8 w-8 rounded-lg cursor-pointer bg-black/60 backdrop-blur-sm text-white/90 hover:bg-black/80 hover:text-white flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-200 border border-white/10"
            title="Open in fullscreen"
          >
            <Maximize className="w-4 h-4" />
          </button>
        </div>
      )}
      <iframe
        src={src}
        title={title}
        allow={allow}
        className={cn('absolute inset-0 w-full h-full border-0', iframeClassName)}
      />
    </div>
  );
}
